import MySettings from '_contexts/types/MySettings';
import {getLFCData, setLFCData} from '_logics/LFCUtil';
import dayjs from 'dayjs';
import Decimal from 'decimal.js-light';
import {useSnackbar} from 'notistack';
import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {useAuthUser, useAuthInitialized} from './AuthUserProvider';

import {setLFCSessionStorage} from './LFCSessionStorage';

// デフォルト値定数
const DEFAULT_PRECISION = 3; // 少数桁数
const DEFAULT_ROUNDING_MODE = Decimal.ROUND_UP; // 丸めモード

// パーセント
const PERCENT_PRECISION = 3; // 少数桁数
const PERCENT_ROUNDING_MODE = Decimal.ROUND_UP; // 丸めモード

// サイズ
const SIZE_PRECISION = 3; // デフォルト少数桁数
const SIZE_ROUNDING_MODE = Decimal.ROUND_UP; // デフォルト丸めモード

// 会社の AWS region 名 → Web API エンドポイントのコード（VITE_EP_WEBAPI_<CODE>）
// m_company.region は AWS region 名（ap-northeast-1 等）で保持され、Cognito custom:region と共用する。
// Web API エンドポイントは現状 AP/US の2種。IN（ap-south-1）は未整備のため AP にフォールバックする。
const REGION_TO_EP_CODE: Record<string, string> = {
  'ap-northeast-1': 'AP',
  'us-east-1': 'US',
  'ap-south-1': 'IN'
};

type MySettingsOperations = {
  regetMySetting: () => Promise<void>;
  saveMySetting: (mys: MySettings) => Promise<void>;
};

/**
 * 設定値のContext（読み取り専用、軽量）
 */
const MySettingsContext = React.createContext<MySettings>({} as MySettings);

/**
 * 初期化状態用のContext
 */
const MySettingsInitializedContext = React.createContext<boolean>(false);

/**
 * 操作用のContext（編集画面でのみ使用）
 */
const MySettingsOperationContext = React.createContext<MySettingsOperations | null>(null);

/**
 * My設定プロバイダ
 */
const MySettingsProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {i18n, t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const authUser = useAuthUser();
  const isAuthInitialized = useAuthInitialized();
  const [mySettings, setMySettings] = useState<MySettings>({} as MySettings);
  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * 認証情報変更時の処理
   */
  useEffect(() => {
    // 初期化が完了するまで待つ
    if (!isAuthInitialized) {
      return;
    }

    if (authUser == null) {
      setMySettings({} as MySettings);
      setIsInitialized(true); // 未認証でも初期化完了とする
      return;
    }
    regetMySetting().finally(() => {
      setIsInitialized(true); // API取得完了後に初期化完了
    });
  }, [authUser, isAuthInitialized]); // regetMySettingは後で定義されるため、依存関係から除外

  /**
   * locale変更時の処理
   */
  useEffect(() => {
    if (!mySettings.locale) return;

    i18n.changeLanguage(mySettings.locale);
    dayjs.locale(mySettings.locale);
    // MUIとEchartsは個別処理する
  }, [mySettings.locale, i18n]);

  /**
   * ブラウザの言語設定を取得
   */
  const getBrowserLanguage = useCallback((): string => {
    return (window.navigator.languages?.[0] || window.navigator.language || 'en').slice(0, 2);
  }, []);

  /**
   * WebAPI URLを設定
   */
  const setWebApiUrl = useCallback((region: string) => {
    // AWS region 名 → エンドポイントコードに変換（旧短縮コード 'ap'/'us' も大文字化でそのまま解決）
    const epCode = REGION_TO_EP_CODE[region] ?? region.toUpperCase();
    const wUrlBase =
      import.meta.env[`VITE_EP_WEBAPI_${epCode}`] || import.meta.env.VITE_EP_WEBAPI_AP;
    const wUrlBaseFull = `${wUrlBase}/${import.meta.env.VITE_ENV}`;
    setLFCSessionStorage({webapiBaseUrl: wUrlBaseFull});
  }, []);

  /**
   * MySettingを再読み込み
   */
  const regetMySetting = useCallback(async () => {
    const data = await getLFCData({
      snack: enqueueSnackbar,
      sql_id: 10001,
      parameters: {},
      name: 'MySetting',
      t
    });

    if (data.length === 0) return;

    const setting: MySettings = {
      isDark: data[0].is_dark,
      locale: data[0].locale || getBrowserLanguage(),
      userName: data[0].user_name,
      companyName: data[0].company_name,
      region: data[0].region || 'ap',
      // 数値精度設定（DBから取得、なければデフォルト値）
      defaultPrecision: data[0].default_precision ?? DEFAULT_PRECISION,
      percentPrecision: data[0].percent_precision ?? PERCENT_PRECISION,
      sizePrecision: data[0].size_precision ?? SIZE_PRECISION,
      // 丸めモード設定（DBから取得、なければデフォルト値）
      defaultRoundingMode: data[0].default_rounding_mode ?? DEFAULT_ROUNDING_MODE,
      percentRoundingMode: data[0].percent_rounding_mode ?? PERCENT_ROUNDING_MODE,
      sizeRoundingMode: data[0].size_rounding_mode ?? SIZE_ROUNDING_MODE
    };

    setWebApiUrl(setting.region || 'ap');
    setMySettings(setting);
  }, [enqueueSnackbar, t, getBrowserLanguage, setWebApiUrl]);

  /**
   * DB保存処理
   */
  const saveMySetting = useCallback(
    async (mys: MySettings) => {
      setMySettings(mys);

      await setLFCData({
        snack: enqueueSnackbar,
        sql_id: 10003,
        parameters: {
          is_dark: mys.isDark,
          locale: mys.locale,
          // 数値精度設定
          default_precision: mys.defaultPrecision,
          percent_precision: mys.percentPrecision,
          size_precision: mys.sizePrecision,
          // 丸めモード設定
          default_rounding_mode: mys.defaultRoundingMode,
          percent_rounding_mode: mys.percentRoundingMode,
          size_rounding_mode: mys.sizeRoundingMode
        },
        name: '',
        t
      });
    },
    [enqueueSnackbar, t]
  );

  const operationsValue = useMemo(
    () => ({regetMySetting, saveMySetting}),
    [regetMySetting, saveMySetting]
  );

  return (
    <MySettingsInitializedContext.Provider value={isInitialized}>
      <MySettingsOperationContext.Provider value={operationsValue}>
        <MySettingsContext.Provider value={mySettings}>{children}</MySettingsContext.Provider>
      </MySettingsOperationContext.Provider>
    </MySettingsInitializedContext.Provider>
  );
};

/**
 * 設定値を取得するフック（読み取り専用、軽量）
 */
export const useMySettings = () => useContext(MySettingsContext);

/**
 * 設定操作を取得するフック（編集画面でのみ使用）
 */
const useMySettingsOperations = () => {
  const operations = useContext(MySettingsOperationContext);
  if (!operations) {
    throw new Error('useMySettingsOperations must be used within a MySettingsProvider');
  }
  return operations;
};

/**
 * 既存APIとの互換性を保つフック
 */
export const useRegetMySetting = () => useMySettingsOperations().regetMySetting;
export const useSaveSetMySetting = () => useMySettingsOperations().saveMySetting;

/**
 * MySettings初期化状態を取得するフック
 */
export const useMySettingsInitialized = () => useContext(MySettingsInitializedContext);

/**
 * 全ての数値フォーマット設定を取得するヘルパーフック
 */
export const useNumericFormatSettings = () => {
  const settings = useMySettings();
  return {
    // 精度設定
    defaultPrecision: settings.defaultPrecision ?? DEFAULT_PRECISION,
    percentPrecision: settings.percentPrecision ?? PERCENT_PRECISION,
    sizePrecision: settings.sizePrecision ?? SIZE_PRECISION,
    // 丸めモード設定
    defaultRoundingMode: settings.defaultRoundingMode ?? DEFAULT_ROUNDING_MODE,
    percentRoundingMode: settings.percentRoundingMode ?? PERCENT_ROUNDING_MODE,
    sizeRoundingMode: settings.sizeRoundingMode ?? SIZE_ROUNDING_MODE
  };
};

export default MySettingsProvider;
